// Utilities for handling user-preferred time zone and time calculations/labels

const LS_KEY = 'gv:timezone';

export function getPreferredTimeZone(): string {
    try {
        const v = localStorage.getItem(LS_KEY);
        if (v) return v;
    } catch { }
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

export function setPreferredTimeZone(tz: string) {
    try { localStorage.setItem(LS_KEY, tz); } catch { }
    try {
        window.dispatchEvent(new CustomEvent('gv:timezoneChanged' as any, { detail: { timeZone: tz } } as any));
    } catch { }
}

export function getSupportedTimeZones(): string[] {
    try {
        // @ts-ignore - supportedValuesOf may not exist in older engines
        if (typeof Intl.supportedValuesOf === 'function') {
            // @ts-ignore
            return Intl.supportedValuesOf('timeZone') as string[];
        }
    } catch { }
    // Fallback, a minimal list
    return [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'Europe/London',
        'Europe/Paris',
        'Asia/Tokyo',
        'Asia/Shanghai',
        'Asia/Kolkata',
        'Australia/Sydney',
    ];
}

// Get hour [0..23] for a given Date in a specific time zone
export function getHourInTimeZone(d: Date, timeZone: string): number {
    try {
        const fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone });
        const parts = (fmt.formatToParts(d));
        const h = parts.find(p => p.type === 'hour')?.value;
        return h ? parseInt(h, 10) : d.getUTCHours();
    } catch {
        return d.getUTCHours();
    }
}

// Format the hour label for a time grid (e.g., 12 AM, 1 AM ...)
export function formatHourLabel(hour: number, timeZone: string, hour12: boolean = true): string {
    try {
        // Use a fixed date so DST rules are applied consistently for label generation
        const base = new Date(Date.UTC(2000, 0, 1, hour, 0, 0));
        const fmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12, timeZone });
        return fmt.format(base);
    } catch {
        // Fallback naive formatting
        if (!hour12) return String(hour).padStart(2, '0');
        if (hour === 0) return '12 AM';
        if (hour < 12) return `${hour} AM`;
        if (hour === 12) return '12 PM';
        return `${hour - 12} PM`;
    }
}

// Get a small label for the current GMT offset in the chosen tz, like GMT-0400
export function getGmtOffsetLabel(timeZone: string, at: Date = new Date()): string {
    try {
        // Some engines support shortOffset which yields GMT-4 etc.
        const fmt = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' as any });
        const s = fmt.format(at);
        const m = s.match(/GMT[+-]\d+/);
        if (m) return m[0];
        // Try extracting from parts
        const parts = (fmt.formatToParts(at));
        const tz = parts.find(p => p.type === 'timeZoneName')?.value;
        if (tz && /GMT/.test(tz)) return tz;
    } catch { }
    return timeZone;
}
